import { Button, Table, Flex, Divider } from 'antd'
import {} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Dispatch, useState } from 'react'

import { useInvestigationsServiceGetListSpecimenInvestigationsEndpoint } from 'src/api/queries'
import { PageBreadcrumbs, RangePicker, SearchBar } from 'src/components'
import { InvestigationsTestsModal } from 'src/features/tests/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { InvestigationTypesInfiniteSelect } from '../components'

import { useStyles } from './styles'

export const LaboratoryInvestigationsPage = () => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const pagination = useTablePagination()

  const { userData } = useUser()

  const [search, setSearch] = useState('')
  const [createdAtFrom, setCreatedAtFrom] = useState('')
  const [createdAtTo, setCreatedAtTo] = useState('')

  const [selectedInvestigationId, setSelectedInvestigationId] =
    useState<string>()
  const [typeId, setTypeId] = useState<string>()

  const [typeIds, setTypeIds] = useState<string[]>([])

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionIds: [String(userData?.currentLegalEntityId)],
    ...(search && { searchTerm: search }),
    ...(createdAtFrom && createdAtTo && { createdAtFrom, createdAtTo }),
    ...(typeIds?.length > 0 && { typeIds })
  }

  const { data, isLoading } =
    useInvestigationsServiceGetListSpecimenInvestigationsEndpoint(queryParams)

  const onFilterChange = (val: string, setState: Dispatch<string>) => {
    setState(val)
    resetPagination(pagination)
  }

  const onCloseTestsModal = () => {
    setSelectedInvestigationId(undefined)
    setTypeId(undefined)
  }

  const columns: ColumnsType = [
    {
      title: t('specimens.code'),
      render: (_, { prefix, specimenInternalCode }) =>
        `${specimenInternalCode}.${prefix}`,
      key: 'code'
    },
    {
      title: t('specimens.type'),
      render: (_, { type }) => type?.translation?.[currentLang?.value],
      key: 'type'
    },
    {
      title: t('specimens.category'),
      render: (_, { category }) => category?.translation?.[currentLang?.value],
      key: 'category'
    },
    {
      key: 'actions',
      render: (_, { id, type }) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedInvestigationId(id)
            setTypeId(type?.id)
          }}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.investigations')}
      items={[{ title: t('menu.investigations') }]}
    >
      <Flex gap={8} wrap="wrap">
        <SearchBar
          onSearch={(val: string) => onFilterChange(val, setSearch)}
          className={styles.search}
        />

        <RangePicker
          format={dayMonthYearFormatWithDot}
          setDateTo={setCreatedAtTo}
          setDateFrom={setCreatedAtFrom}
          resetPagination={() => resetPagination(pagination)}
          placeholder={[
            t('tests.created_from_date'),
            t('tests.created_to_date')
          ]}
        />

        <InvestigationTypesInfiniteSelect onChange={(val) => setTypeIds(val)} />
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        dataSource={data?.data}
        loading={isLoading}
        columns={columns}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {selectedInvestigationId && typeId && (
        <InvestigationsTestsModal
          investigationId={selectedInvestigationId}
          typeId={typeId}
          onCancel={onCloseTestsModal}
        />
      )}
    </PageBreadcrumbs>
  )
}
